import {z} from 'zod';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {
  MAX_SITE_BRANCH_DESCRIPTION_REQUEST_LENGTH,
  MAX_SITE_BRANCH_NAME_LENGTH,
} from '@packages/domains/change-coordination/branches/data-types';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'update-site-branch-details' as const;

const argumentZod = z.object({
  siteBranchId: z
    .string()
    .min(1)
    .describe('Site-branch id from list-site-branches.'),
  name: z
    .string()
    .min(1)
    .max(MAX_SITE_BRANCH_NAME_LENGTH)
    .optional()
    .describe('New branch name. Omit to keep the current name.'),
  description: z
    .string()
    .max(MAX_SITE_BRANCH_DESCRIPTION_REQUEST_LENGTH)
    .optional()
    .describe(
      'New branch description. Omit to keep the current description; pass an empty string to clear it.'
    ),
});

export const updateSiteBranchDetailsManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Update site branch details',
  family: 'site-branches',
  llmTool: {
    name: toolName,
    description:
      'Renames a live site-scope branch (Releases) or changes its description for the current site. Send name, description, or both; returns the updated {id, name, description}. Merged branches cannot be updated (409). Does not pull changes from main.',
    inputSchema: argumentZod,
  },
  type: 'server',
  featureGate: FEATURE_FLAGS.SITE_BRANCHES,
  requiredScopes: ['branches:update'],
  permission: [
    {
      resource: 'SiteBranch',
      action: 'manage',
      resolveResources: (args) => [{type: 'SiteBranch', id: args.siteBranchId}],
    },
  ],
  confirmBeforeExecuting: false,
  isConcurrencySafe: () => false,
  isReadOnly: () => false,
});

export type UpdateSiteBranchDetailsArgumentType = z.infer<typeof argumentZod>;
